import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  MapPinIcon,
  PaperClipIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../utils/config';
import { api } from '../utils/api';

// =========================
// Incident types & options
// =========================
const INCIDENT_TYPES = {
  Security: [
    'Physical violence (1)',
    'Homicide (2)',
    'Robbery (3)',
    'Destruction of property (4)',
    'Arson (5)',
    'Demonstrations (peaceful or violent) (6)',
    'Drugs and trafficking (7)',
    'Ritualistic killings (8)'
  ],
  'Health and environment': [
    'Disease outbreaks (Covid, Ebola, M Pox, Cholera etc.) (1)',
    'Natural disasters (2)',
    'Environmental pollution or illegal dumping (3)'
  ],
  Gender: [
    'Physical violence (1)',
    'Psychological violence (2)',
    'Sexual violence (3)',
    'Restrictions and lack of equal opportunities (4)',
    'Cyber-bullying (5)'
  ],
  'Governance and human rights violations': [
    'Corruption and bribery (1)',
    'Land/boundary disputes (2)',
    'Torture, ill-treatment (3)',
    'Non-discrimination and equality (4)',
    'Presumption of innocence (5)',
    'Accountability (6)',
    'Equal participation (7)',
    'Effective remedy (8)',
    'Access to Education (9)',
    'Health and safety (10)',
    'Right to Peaceful assembly (11)',
    'Illegal Arrests (12)',
    'Use of force (13)',
    'Expression of views (14)',
    'Access to information (15)',
    'Access to basic social services (roads, food, education, electricity, safe drinking water, etc.) (16)'
  ],
  'Campaign/Elections': [
    'Fraud and irregularities (1)',
    'Vote buying (2)',
    'Voter intimidation and coercion (3)',
    'Intimidation or attacks on polling workers (4)',
    'Disruption of polling at the Polling Station (5)',
    'Quarrels among voters in the queue (6)',
    'Fights among voters in the queue (7)',
    'Incidents involving voters and security personnel (8)',
    'People fainting or collapsing at the polling station (9)',
    'Injuries reported at the polling station (10)',
    'Medical emergencies at the polling station (11)',
    'Campaigning by party representatives near the polling station (12)',
    'Vote buying by party representatives (13)',
    'Violence against women in elections (14)',
    'Destruction of polling material or installations (15)',
    'Voter trucking (16)',
    'Observer Obstruction (17)',
    'Illegal Campaign Activities (18)'
  ],
  'Media and Information': ['Fake News (1)', 'Hate Speech (2)', 'Biased Coverage (3)', 'Censorship (4)'],
  'Children under 18 years': [
    'Physical violence (1)',
    'Psychological violence (2)',
    'Sexual Violence (3)',
    'Abduction (4)',
    'Restrictions or lack of access to essential social services (5)',
    "Use of children for political parties or candidates' promotional campaign, road shows, rallies, (6)",
    'Use of children as election worker by the political parties (7)'
  ]
} as const;

const AFFECTED_GROUPS = [
  'Men',
  'Women',
  'Person with disabilities',
  'Person with albinism',
  'Elderly',
  'Youth',
  'Children under 18'
] as const;

// =========================
// Types
// =========================
interface IncidentReportFormProps {
  incident?: any;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

interface DistrictRow { id: number; name: string }
interface ConstituencyRow { id: number; name: string; district_id: number }
interface WardRow { id: number; name: string; constituency_id: number }
interface PollingCenterRow { id: number; name: string; ward_id: number; center_number?: number }

// =========================
// Component
// =========================
const IncidentReportForm: React.FC<IncidentReportFormProps> = ({ incident, onClose, onSubmit }) => {
  const { token, user } = useAuth();

  const [formData, setFormData] = useState({
    reporter_name: '',
    title: '',
    description: '',
    category: '',
    subcategory: '',
    severity: '',
    district: '',
    constituency: '',
    ward: '',
    center: '',
    location_lat: '',
    location_lng: '',
    affected_groups: [] as string[],
    report_date: '',
    report_time: '',
    submission_method: 'app'
  });

  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Lookups (strictly filtered by district/constituency/ward like Step1)
  const [geo, setGeo] = useState<{
    districts: DistrictRow[];
    constituencies: ConstituencyRow[];
    wards: WardRow[];
    pollingcenters: PollingCenterRow[];
  }>({ districts: [], constituencies: [], wards: [], pollingcenters: [] });

  const [selectedIds, setSelectedIds] = useState<{
    district_id?: number;
    constituency_id?: number;
    ward_id?: number;
  }>({});

  const [effectiveIds, setEffectiveIds] = useState<{
    district_id: number | null | undefined;
    constituency_id: number | null | undefined;
    ward_id: number | null | undefined;
  }>({ district_id: null, constituency_id: null, ward_id: null });

  // =====================================
  // Fetch lookups (ID + name aware)
  // =====================================
  const fetchLookups = useCallback(async (opts?: {
    district?: string;
    constituency?: string;
    ward?: string;
    district_id?: number;
    constituency_id?: number;
    ward_id?: number;
    include_all_polling?: '0' | '1';
  }) => {
    try {
      const params: any = {};
      if (opts?.district) params.district = opts.district;
      if (opts?.constituency) params.constituency = opts.constituency;
      if (opts?.ward) params.ward = opts.ward;
      if (opts?.district_id) params.district_id = String(opts.district_id);
      if (opts?.constituency_id) params.constituency_id = String(opts.constituency_id);
      if (opts?.ward_id) params.ward_id = String(opts.ward_id);
      if (opts?.include_all_polling) params.include_all_polling = opts.include_all_polling;

      const resp = await api.dashboard.getOverview(params);
      const lookups = resp?.data?.data?.lookups || {
        districts: [],
        constituencies: [],
        wards: [],
        pollingcenters: []
      };

      // (optional) keep backend-resolved effective ids (useful when names are ambiguous)
      setEffectiveIds({
        district_id: (lookups as any)?.effectiveDistrictId ?? null,
        constituency_id: (lookups as any)?.effectiveConstituencyId ?? null,
        ward_id: (lookups as any)?.effectiveWardId ?? null
      });

      const sorted = {
        districts: (lookups.districts || []).sort((a: DistrictRow, b: DistrictRow) => String(a.name).localeCompare(String(b.name))),
        constituencies: (lookups.constituencies || []).sort((a: ConstituencyRow, b: ConstituencyRow) => String(a.name).localeCompare(String(b.name))),
        wards: (lookups.wards || []).sort((a: WardRow, b: WardRow) => String(a.name).localeCompare(String(b.name))),
        pollingcenters: (lookups.pollingcenters || []).sort((a: PollingCenterRow, b: PollingCenterRow) => String(a.name).localeCompare(String(b.name)))
      };

      setGeo(sorted);
      return sorted;
    } catch (error) {
      console.error('Error fetching lookups:', error);
      toast.error('Failed to load location data');
      const empty = { districts: [], constituencies: [], wards: [], pollingcenters: [] };
      setGeo(empty);
      return empty;
    }
  }, []);

  // =====================================
  // Initialize with user's profile location
  // =====================================
  useEffect(() => {
    const d = (user?.district || '').trim();
    const c = (user?.constituency || '').trim();
    const w = (user?.ward || '').trim();

    setFormData(prev => ({ ...prev, district: d, constituency: c, ward: w }));

    if (d && c && w) {
      // Same pattern as Step1LocationInfo: include_all_polling loads PCs
      fetchLookups({ district: d, constituency: c, ward: w, include_all_polling: '1' });
    } else {
      setGeo(prev => ({ ...prev, pollingcenters: [] }));
      setFormData(prev => ({ ...prev, center: '' }));
    }
  }, [user, fetchLookups]);

  // =====================================
  // Pre-fill form if editing
  // =====================================
  useEffect(() => {
    if (incident) {
      setFormData({
        reporter_name: incident.reporter_name || '',
        title: incident.title || '',
        description: incident.description || '',
        category: incident.category || '',
        subcategory: incident.subcategory || '',
        severity: incident.severity || '',
        district: incident.district || (user?.district || ''),
        constituency: incident.constituency || (user?.constituency || ''),
        ward: incident.ward || (user?.ward || ''),
        center: incident.center || '',
        location_lat: incident.location_lat || '',
        location_lng: incident.location_lng || '',
        affected_groups: incident.affected_groups || [],
        report_date: incident.report_date || '',
        report_time: incident.report_time || '',
        submission_method: 'app'
      });

      // Hydrate PCs for the incident's location too
      if (incident.district && incident.constituency && incident.ward) {
        fetchLookups({
          district: incident.district,
          constituency: incident.constituency,
          ward: incident.ward,
          include_all_polling: '1'
        });
      }
    } else {
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().slice(0, 5);
      setFormData(prev => ({ ...prev, report_date: currentDate, report_time: currentTime }));
    }
  }, [incident, fetchLookups, user]);

  // =====================================
  // Validation
  // =====================================
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) newErrors.title = 'Incident title is required';
    else if (formData.title.length > 100) newErrors.title = 'Title must be less than 100 characters';

    if (!formData.description.trim()) newErrors.description = 'Incident description is required';
    else if (formData.description.length > 500) newErrors.description = 'Description must be less than 500 characters';

    if (!formData.category) newErrors.category = 'Please select a category';
    if (!formData.subcategory) newErrors.subcategory = 'Please select a subcategory';
    if (!formData.severity) newErrors.severity = 'Please select a severity level';

    if (!formData.district.trim()) newErrors.district = 'District is required (from your user profile)';
    if (!formData.constituency.trim()) newErrors.constituency = 'Constituency is required (from your user profile)';
    if (!formData.ward.trim()) newErrors.ward = 'Ward is required (from your user profile)';

    if (!formData.center.trim()) newErrors.center = 'Center/Location is required';
    else if (!geo.pollingcenters.some(pc => pc.name === formData.center)) {
      newErrors.center = 'Please pick a center from the list for your ward';
    }

    if (formData.affected_groups.length === 0) newErrors.affected_groups = 'Please select at least one affected group';
    if (!formData.report_date) newErrors.report_date = 'Report date is required';
    if (!formData.report_time) newErrors.report_time = 'Report time is required';

    if (
      formData.location_lat &&
      (parseFloat(formData.location_lat) < -90 || parseFloat(formData.location_lat) > 90)
    ) newErrors.location_lat = 'Latitude must be between -90 and 90';

    if (
      formData.location_lng &&
      (parseFloat(formData.location_lng) < -180 || parseFloat(formData.location_lng) > 180)
    ) newErrors.location_lng = 'Longitude must be between -180 and 180';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // =====================================
  // Handlers
  // =====================================
  const handleInputChange = (field: string, value: string) => {
    // generic update
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));

    if (field === 'category') {
      setFormData(prev => ({ ...prev, subcategory: '' }));
    }

    // Admin/edge case edits (normally read-only)
    if (field === 'district') {
      const districtName = value.trim();
      const row = geo.districts.find(d => d.name === districtName);
      const district_id = row?.id;
      setSelectedIds({ district_id, constituency_id: undefined, ward_id: undefined });
      fetchLookups({ district: districtName, district_id, include_all_polling: '1' });
      setFormData(prev => ({ ...prev, district: districtName, constituency: '', ward: '', center: '' }));
      return;
    }

    if (field === 'constituency') {
      const constituencyName = value.trim();
      const row = geo.constituencies.find(c => c.name === constituencyName);
      const constituency_id = row?.id;
      setSelectedIds(prev => ({ ...prev, constituency_id, ward_id: undefined }));
      fetchLookups({
        district: formData.district,
        district_id: selectedIds.district_id,
        constituency: constituencyName,
        constituency_id,
        include_all_polling: '1'
      });
      setFormData(prev => ({ ...prev, constituency: constituencyName, ward: '', center: '' }));
      return;
    }

    if (field === 'ward') {
      const wardName = value.trim();
      const row = geo.wards.find(
        w => w.name === wardName && (!selectedIds.constituency_id || w.constituency_id === selectedIds.constituency_id)
      );
      const ward_id = row?.id;
      setSelectedIds(prev => ({ ...prev, ward_id }));
      fetchLookups({
        district: formData.district,
        district_id: selectedIds.district_id,
        constituency: formData.constituency,
        constituency_id: selectedIds.constituency_id,
        ward: wardName,
        ward_id,
        include_all_polling: '1'
      });
      setFormData(prev => ({ ...prev, ward: wardName, center: '' }));
      return;
    }

    if (field === 'center') {
      setFormData(prev => ({ ...prev, center: value }));
      return;
    }
  };

  // Clear stale center when ward/filters change
  useEffect(() => {
    if (!formData.center) return;
    const exists = geo.pollingcenters.some(pc => pc.name === formData.center);
    if (!exists) {
      setFormData(prev => ({ ...prev, center: '' }));
      if (formData.district && formData.constituency && formData.ward) {
        toast.error('Selected center is not in your ward. Please re-select.');
      }
    }
  }, [geo.pollingcenters, formData.center, formData.district, formData.constituency, formData.ward]);

  const handleAffectedGroupsChange = (groupName: string) => {
    setFormData(prev => {
      const exists = prev.affected_groups.includes(groupName);
      const affected_groups = exists
        ? prev.affected_groups.filter(g => g !== groupName)
        : [...prev.affected_groups, groupName];
      return { ...prev, affected_groups };
    });
    if (errors.affected_groups) setErrors(prev => ({ ...prev, affected_groups: '' }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }
    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      position => {
        setFormData(prev => ({
          ...prev,
          location_lat: position.coords.latitude.toString(),
          location_lng: position.coords.longitude.toString()
        }));
        setIsGettingLocation(false);
        toast.success('Location captured successfully');
      },
      error => {
        setIsGettingLocation(false);
        toast.error('Failed to get current location');
        console.error('Geolocation error:', error);
      }
    );
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => {
      const validTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'video/mp4',
        'video/mov',
        'application/pdf',
        'application/msword'
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Unsupported file type`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File size too large (max 10MB)`);
        return false;
      }
      return true;
    });

    if (attachments.length + validFiles.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // =====================================
  // Submit
  // =====================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const sanitizeValue = (value: string | undefined | null): string => {
        if (value === undefined || value === null || value === '') return '';
        return String(value);
      };

      const formDataToSend = new FormData();

      formDataToSend.append('reporter_name', sanitizeValue(user?.name) || 'Unknown User');
      formDataToSend.append('title', sanitizeValue(formData.title));
      formDataToSend.append('description', sanitizeValue(formData.description));
      formDataToSend.append('category', sanitizeValue(formData.category));
      formDataToSend.append('subcategory', sanitizeValue(formData.subcategory));
      formDataToSend.append('severity', sanitizeValue(formData.severity));
      formDataToSend.append('district', sanitizeValue(formData.district));
      formDataToSend.append('constituency', sanitizeValue(formData.constituency));
      formDataToSend.append('ward', sanitizeValue(formData.ward));
      formDataToSend.append('center', sanitizeValue(formData.center));
      formDataToSend.append('affected_groups', JSON.stringify(formData.affected_groups || []));
      formDataToSend.append('report_date', sanitizeValue(formData.report_date));
      formDataToSend.append('report_time', sanitizeValue(formData.report_time));
      formDataToSend.append('submission_method', sanitizeValue(formData.submission_method) || 'app');

      if (formData.location_lat && formData.location_lat.trim() !== '') {
        formDataToSend.append('location_lat', sanitizeValue(formData.location_lat));
      }
      if (formData.location_lng && formData.location_lng.trim() !== '') {
        formDataToSend.append('location_lng', sanitizeValue(formData.location_lng));
      }

      attachments.forEach(file => {
        formDataToSend.append('files', file);
      });

      const authToken = token || localStorage.getItem('token') || '';

      const response = await fetch(`${getApiBaseUrl()}/incidents/with-files`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          let errorMessage = 'Failed to submit incident';
          if (errorData.details && Array.isArray(errorData.details)) {
            errorMessage += ': ' + errorData.details.join(', ');
          } else if (errorData.error) {
            errorMessage += ': ' + errorData.error;
          } else if (errorData.message) {
            errorMessage += ': ' + errorData.message;
          }
          throw new Error(errorMessage);
        } catch (parseError: any) {
          if (parseError instanceof Error && parseError.message.startsWith('Failed to submit incident')) {
            throw parseError;
          }
          throw new Error('Failed to submit incident');
        }
      }

      const result = await response.json();
      toast.success('Incident report submitted successfully!');
      onSubmit(result);
    } catch (error) {
      console.error('Submission error:', error);
      if (error instanceof Error) toast.error(error.message);
      else toast.error('Failed to submit incident');
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableSubCategories = formData.category
    ? (INCIDENT_TYPES as any)[formData.category] || []
    : [];

  // =========================
  // Render
  // =========================
  return (
    <div className="min-h-screen py-4 bg-gray-50">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 bg-white rounded-lg shadow">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {incident ? 'Edit Incident Report' : 'New Incident Report'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Report incidents for election observation and early warning system
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Information */}
          <div className="card">
            <div className="pb-3 mb-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Basic Information</h2>
              <p className="text-sm text-gray-500">Provide basic details about the incident</p>
            </div>

            <div className="space-y-4">
              {/* Single Row - Title, Category, Sub Category, Severity */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label htmlFor="title" className="block mb-1 text-sm font-medium text-gray-700">
                    Incident Title <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={e => handleInputChange('title', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                      errors.title ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Brief title describing the incident..."
                    maxLength={100}
                  />
                  {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
                  <p className="mt-1 text-xs text-gray-500">{formData.title.length}/100</p>
                </div>

                <div>
                  <label htmlFor="category" className="block mb-1 text-sm font-medium text-gray-700">
                    Category <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={e => handleInputChange('category', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                      errors.category ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select category...</option>
                    <option value="Security">Security</option>
                    <option value="Health and environment">Health and environment</option>
                    <option value="Gender">Gender</option>
                    <option value="Governance and human rights violations">Governance and human rights violations</option>
                    <option value="Campaign/Elections">Campaign/Elections</option>
                    <option value="Media and Information">Media and Information</option>
                    <option value="Children under 18 years">Children under 18 years</option>
                  </select>
                  {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
                </div>

                <div>
                  <label htmlFor="subcategory" className="block mb-1 text-sm font-medium text-gray-700">
                    Sub Category <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="subcategory"
                    value={formData.subcategory}
                    onChange={e => handleInputChange('subcategory', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                      errors.subcategory ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={!formData.category}
                  >
                    <option value="">Select subcategory...</option>
                    {availableSubCategories.map((subCat: string) => (
                      <option key={subCat} value={subCat}>
                        {subCat}
                      </option>
                    ))}
                  </select>
                  {errors.subcategory && <p className="mt-1 text-xs text-red-600">{errors.subcategory}</p>}
                </div>

                <div>
                  <label htmlFor="severity" className="block mb-1 text-sm font-medium text-gray-700">
                    Severity Level <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="severity"
                    value={formData.severity}
                    onChange={e => handleInputChange('severity', e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.severity ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select severity level</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  {errors.severity && <p className="mt-1 text-xs text-red-600">{errors.severity}</p>}
                </div>
              </div>

              {/* Description row */}
              <div>
                <label htmlFor="description" className="block mb-1 text-sm font-medium text-gray-700">
                  Incident Description <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={e => handleInputChange('description', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Provide detailed description of the incident..."
                  maxLength={500}
                />
                {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
                <p className="mt-1 text-xs text-gray-500">{formData.description.length}/500 characters</p>
              </div>

              {/* Affected Groups and Report Date/Time row */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {/* Affected Groups */}
                <div className="lg:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Affected Groups <span className="text-red-600">*</span> (Select multiple)
                  </label>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    {AFFECTED_GROUPS.map(group => (
                      <label key={group} className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.affected_groups.includes(group)}
                          onChange={() => handleAffectedGroupsChange(group)}
                          className="text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-gray-700">{group}</span>
                      </label>
                    ))}
                  </div>
                  {errors.affected_groups && (
                    <p className="mt-1 text-xs text-red-600">{errors.affected_groups}</p>
                  )}
                  {formData.affected_groups.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      Selected: {formData.affected_groups.join(', ')}
                    </p>
                  )}
                </div>

                {/* Report Date and Time */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="report_date" className="block mb-1 text-sm font-medium text-gray-700">
                      Report Date <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="date"
                      id="report_date"
                      value={formData.report_date}
                      onChange={e => handleInputChange('report_date', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                        errors.report_date ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.report_date && <p className="mt-1 text-xs text-red-600">{errors.report_date}</p>}
                  </div>

                  <div>
                    <label htmlFor="report_time" className="block mb-1 text-sm font-medium text-gray-700">
                      Report Time <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="time"
                      id="report_time"
                      value={formData.report_time}
                      onChange={e => handleInputChange('report_time', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                        errors.report_time ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.report_time && <p className="mt-1 text-xs text-red-600">{errors.report_time}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="card">
            <div className="pb-3 mb-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Location Information</h2>
              <p className="text-sm text-gray-500">Your assigned location and available centers</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* User's assigned location - Read-only */}
              <div>
                <label htmlFor="district" className="block mb-1 text-sm font-medium text-gray-700">
                  Your District <span className="text-gray-400">(from profile)</span>
                </label>
                <input
                  type="text"
                  id="district"
                  value={formData.district}
                  readOnly
                  className="w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-md cursor-not-allowed bg-gray-50"
                  placeholder="Loading from your profile..."
                />
                {errors.district && <p className="mt-1 text-xs text-red-600">{errors.district}</p>}
              </div>

              <div>
                <label htmlFor="constituency" className="block mb-1 text-sm font-medium text-gray-700">
                  Your Constituency <span className="text-gray-400">(from profile)</span>
                </label>
                <input
                  type="text"
                  id="constituency"
                  value={formData.constituency}
                  readOnly
                  className="w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-md cursor-not-allowed bg-gray-50"
                  placeholder="Loading from your profile..."
                />
                {errors.constituency && <p className="mt-1 text-xs text-red-600">{errors.constituency}</p>}
              </div>

              <div>
                <label htmlFor="ward" className="block mb-1 text-sm font-medium text-gray-700">
                  Your Ward <span className="text-gray-400">(from profile)</span>
                </label>
                <input
                  type="text"
                  id="ward"
                  value={formData.ward}
                  readOnly
                  className="w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-md cursor-not-allowed bg-gray-50"
                  placeholder="Loading from your profile..."
                />
                {errors.ward && <p className="mt-1 text-xs text-red-600">{errors.ward}</p>}
              </div>

              {/* Center selection for user's area */}
              <div className="md:col-span-3">
                <label htmlFor="center" className="block mb-1 text-sm font-medium text-gray-700">
                  Select Center/Location in Your Area <span className="text-red-600">*</span>
                </label>
                <select
                  id="center"
                  value={formData.center}
                  onChange={e => handleInputChange('center', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                    errors.center ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={!formData.district || !formData.constituency || !formData.ward}
                >
                  <option value="">
                    {!formData.district || !formData.constituency || !formData.ward
                      ? 'Loading centers for your area...'
                      : 'Select a center in your area...'}
                  </option>
                  {geo.pollingcenters.map(pc => (
                    <option key={pc.id} value={pc.name}>
                      {pc.name}
                    </option>
                  ))}
                </select>
                {errors.center && <p className="mt-1 text-xs text-red-600">{errors.center}</p>}
                {formData.district && formData.constituency && formData.ward && (
                  <p className="mt-1 text-xs text-blue-600">
                    📍 Showing {geo.pollingcenters.length} centers in {formData.district} → {formData.constituency} → {formData.ward}
              
                  </p>
                )}
              </div>

              <div className="md:col-span-2 xl:col-span-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">GPS Coordinates (Optional)</label>
                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <input
                      type="number"
                      id="location_lat"
                      value={formData.location_lat}
                      onChange={e => handleInputChange('location_lat', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                        errors.location_lat ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Latitude (e.g., -15.7861)"
                      step="any"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="number"
                      id="location_lng"
                      value={formData.location_lng}
                      onChange={e => handleInputChange('location_lng', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                        errors.location_lng ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Longitude (e.g., 35.0058)"
                      step="any"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    disabled={isGettingLocation}
                    className="flex items-center px-4 py-2 space-x-1 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    <MapPinIcon className="w-4 h-4" />
                    <span>{isGettingLocation ? 'Getting...' : 'Get GPS'}</span>
                  </button>
                </div>
                {(errors.location_lat || errors.location_lng) && (
                  <p className="mt-1 text-xs text-red-600">{errors.location_lat || errors.location_lng}</p>
                )}
              </div>
            </div>
          </div>
          <div className="card">
            <div className="pb-3 mb-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Attachments (Optional)</h2>
              <p className="text-sm text-gray-500">Upload supporting documents, images, or videos (max 5 files, 10MB each)</p>
            </div>

            <div>
              <input
                type="file"
                id="attachments"
                multiple
                accept="image/*,video/mp4,video/mov,application/pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
              />
              <label
                htmlFor="attachments"
                className="flex items-center justify-center w-full p-6 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-gray-400"
              >
                <div className="text-center">
                  <PaperClipIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">Click to upload files or drag and drop</p>
                  <p className="text-xs text-gray-500">Images, Videos, PDFs, Documents (max 10MB each)</p>
                </div>
              </label>

              {attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <PaperClipIcon className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end py-6 space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : incident ? 'Update Report' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IncidentReportForm;